import { ColDef, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import { camelCase, filter, flatten, forEach, map, uniq } from "lodash";
import { dateFormat } from "../../library/constants/dateFormat";
import { DateColumnDef, EmailLinkColumnDef, LinkColumnDef, MapLinkColumnDef, StringColumnDef } from "../../library/util/gridColumnFactory";
import { ILookupItem } from "../../types/genericTypes";
import { IQueryBuilder } from "../slices/querySlice";

export const mapQueryListAttributes = (queryList: any[], filterExpanded = false): any[] => {
    const returnValue = queryList.map((query) => {
        const { name, id, ownerId, taxonomyName, updatedAt, nodes, searchQuery, ...remaining } = query;
        return {
            ...remaining,
            id,
            name,
            ownerId,
            taxonomyName,
            updatedAt,
            nodes,
            searchQuery
        }   
    });
    if (filterExpanded) {
        return returnValue.filter((value) => !value.expanded);
    }
    return returnValue;
}

export const transformFiltersForDataBinding = (filters: { key: string, constraint: string, value: string}[]): { key: string, constraint: string, value: string}[] => {
    return filters.map((filter) => {
        const { key, value, constraint } = filter;
        return {
            key: transformFilterLabelForDisplay(key),
            constraint: transformFilterLabelForDisplay(constraint) || 'Equals',
            value
        }
    })
}

export const transformQueryBuilderForDisplay = (queryBuilder: any): IQueryBuilder => {
    return {
        ...queryBuilder,
        filters: queryBuilder.filters || [],
        labels: transformFilterLabelsForDisplay(queryBuilder.labels || []),
        searchQuery: '',
        queryDepth: 1
    }
}

export const transformQueryBuilderForQuery = (queryBuilder: IQueryBuilder): any => {
    const returnValue = {
        ...queryBuilder,
        filters: queryBuilder.filters?.length ? transformFilterKeyValuesForQuery(queryBuilder.filters) : [],
        labels: queryBuilder.labels?.length ? transformFilterLabelsForQuery(queryBuilder.labels) : []
    };
    return returnValue;
}

const transformFilterKeyValuesForQuery = (filters: { key: string, constraint: string, value: string }[]) => {
    return filters?.length ? filter(map(filters, (filter) => ({
        ...filter,
        constraint: camelCase(filter.constraint || ''),
        key: camelCase(filter.key)
    })), (filter) => !!filter.key?.length) : [];
}

export const transformFilterLabelsForDisplay = (labels: string[]) => {
    return map(labels, ((label: string) => {
        return transformFilterLabelForDisplay(label);
    }));
}

export const transformFilterLabelForDisplay = (label: string) => {
    const result = (label || '').replace(/([A-Z])/g, ' $1');
    const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
    return finalResult;
}

export const transformFilterScoreLabelForDisplay = (label: string) => {
    const result = (label || '').replace(/_/g, ' ');
    const finalResult = result.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    return finalResult;
};
const transformFilterLabelsForQuery = (labels: string[]) => {
    return map(labels, (str) => {
        return camelCase(str);
    })
};

export const getValidDisplayParams = (labels: string[]): string[] => {
    const valuesToIgnore = [
        'id',
        'externalId',
        'catalogId',
        'documentId',
        'href',
        'schemaUrl',
        'version',
        'deleted',
        'identity',
        'aggregateRating',
        'schemaName',
        'updatedBy',
        'taxonomyName',
        'nodeType',
        'globalLocationNumber',
        'existent',
        'entityType',
        'tag_suggest'
    ]
    return filter(labels, (str) => valuesToIgnore.indexOf(str) < 0);
}

export const indexQueryResults = (nodes: any[]) => {
    let returnValue = [...nodes];
    returnValue = map(returnValue, (node, index) => ({...node, index: index + 1}));
    return returnValue;
}

export const getValueKeys = (nodes: any[]) => {
    const arrayOfKeys: string[][] = nodes.map((rowItem) => {
        const keys = Object.keys(rowItem);
        const returnValue = [];
        for (let key of keys) {
            if (rowItem[key] && rowItem[key]?.length) {
                returnValue.push(key);
            } else if (rowItem[key] && !Array.isArray(rowItem[key])) {
                returnValue.push(key);
            }
        }   
        return returnValue;
    });
    const providedDataKeys = uniq(flatten(arrayOfKeys) || []);
    return !!nodes?.length ? getValidDisplayParams(providedDataKeys) : [];
}

export const createDefaultColumnDefinition = (labels: string[]): ColDef[] => {
    const columns: ColDef[] = [];
    forEach(labels, (str) => {
        // if (str === 'popularityScore') {
        //     columns.push({
        //         ...StringColumnDef(str, 'Confidence', false, 110),
        //         sort:'desc',
        //         valueFormatter: (params: ValueFormatterParams) => {
        //             return `${params.data.popularityScore}%`
        //         },
        //         type: 'rightAligned',
        //         width: 110,
        //         suppressSizeToFit: true,
        //         pinned: 'left'
        //     });
        //     return
        // } else 
        if (str === 'name') {
            columns.push({
                ...StringColumnDef(str, 'Name', true),
                pinned: 'left',
                suppressSizeToFit: true,
                // checkboxSelection: true,
                // headerCheckboxSelection: true,
                // headerCheckboxSelectionFilteredOnly: true,
            });
            return;
        } else if (str === 'url') {
            columns.push({
                ...LinkColumnDef(str, 'URL', true)
            });
            return;
        } else if (str === 'source') {
            columns.push({
                ...LinkColumnDef(str, 'Source', true)
            });
            return;
        } else if (str === 'updatedAt' || str === 'created' || str === 'create' || str === 'updated') {
            columns.push({
                ...DateColumnDef(str, getLabelValue(str), dateFormat, true, 200)
            });
            return;
        } else if (str.toLowerCase().includes('location') || str.toLowerCase().includes('address')) {
            columns.push({
                ...MapLinkColumnDef(str, getLabelValue(str), true)
            })
            return;
        } else if (str.toLowerCase().includes('index')) {
            return;
        } else if (str.toLowerCase().includes('email')) {
            columns.push({
                ...EmailLinkColumnDef(str, 'Email', true, 200)
            });
            return;
        } else if (str.toLowerCase().includes('content_length')) {
            columns.push({
                ...StringColumnDef(str, 'Content Length', true)
            });
            return;
        } else if (str.toLowerCase().includes('content_type')) {
            columns.push({
                ...StringColumnDef(str, 'Content Type', true)
            });
            return;
        } else if (str.toLowerCase().includes('creator_tool')) {
            columns.push({
                ...StringColumnDef(str, 'Creator Tool', true)
            });
            return;
        }
        columns.push(StringColumnDef(str, getLabelValue(str), true, 200));
    })
    // return map(labels, (str) => {
    //     return StringColumnDef(str, getLabelValue(str), true, 200)
    // });
    return columns;
}

export const getLabelValue = (key: string): string => {
  const result = key.replace(/([A-Z])/g, ' $1');
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
};

// const isJSONString = (str: string) => {
//     try {
//         JSON.parse(str);
//     } catch (e) {
//         return false;
//     }
//     return true;
// }

export const transformSuggestions = (nodes: any[], queryString: string) => {
    const nodeList: any[] = [];
    nodes.forEach((node: any) => {
        const matchesSubString = (node.name as string).toLowerCase().includes(queryString.toLowerCase());
        if (matchesSubString) {
            nodeList.push(node.name)
        }
        // const keys = Object.keys(node);
        // for (let key of keys) {
        //     const value = node[key];
        //     if (typeof value === 'string') {
        //         if (!isJSONString(value) && (value[0] !== '{' && value[0] !== '[')) {
                    
        //             if (matchesSubString) {
        //                 nodeList.push(value);
        //             }
        //         }
        //     }
        // }
    });
    // filter out duplicates in the suggestion list.)
    return uniq(nodeList);
}

export const transformFiltersForDisplay = (filters: any[], taxonomyName: string, searchQuery: string) => {
    let returnValue = [];
    if (!!filters.length) {
        returnValue = [...filters];
        returnValue.unshift({
            subject: 'Taxonomy Name',
            type: 'property',
            value: taxonomyName,
            key: 'taxonomyName'
        })
        return returnValue;
    } else if (!!searchQuery?.length) {
        return [
            {
                subject: 'Taxonomy Name',
                type: 'property',
                value: taxonomyName,
                key: 'taxonomyName'
            },{
                subject: 'Search Query',
                type: 'search_query',
                value: searchQuery,
                key: 'searchQuery'
            }
        ]
    } else {
        return [];
    }
}
